import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';

import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { translate } from '@jsverse/transloco';
import { DropdownObject } from '@inmoment/the-kitchen';

import { getSectionFilters } from 'app/modules/collection/services/filter.service';
import {
  WidgetGroupStore,
  getDashboardLevelWidgetGroup,
} from 'app/modules/dashboard/stores/widget-group.store';
import { WidgetGroup, Widget, Dashboard } from 'app/providers/bridge-api/dashboard.service';
import { AlertService } from 'app/services/alert.service';
import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';
import { hasSectionFilters } from '../widget/widget.component';

@EnableClassTelemetry()
@Component({
  selector: 'app-change-widget-group-dialog',
  templateUrl: './change-widget-group-dialog.component.html',
  styleUrls: ['./change-widget-group-dialog.component.scss'],
  standalone: false,
})
export class ChangeWidgetGroupDialogComponent implements OnInit {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  @Input() widget: Widget;
  @Input() isAdding: boolean;
  @Input() forParents: boolean;
  @Input() public dashboard: Dashboard;

  @Output() modalClosed: EventEmitter<any> = new EventEmitter<any>();

  changeDFGForm: UntypedFormGroup;
  currentGroupObject: WidgetGroup;
  widgetGroups: DropdownObject[] = [];
  canAddToIPWG = true;

  private readonly widgetGroupStore = inject(WidgetGroupStore);

  constructor(
    private formBuilder: UntypedFormBuilder,
    private alertService: AlertService
  ) {
    this.changeDFGForm = this.formBuilder.group({
      filterGroup: [],
    });
  }

  ngOnInit(): void {
    let groups = this.widgetGroupStore.getByPage(this.widget.payload.page);

    if (this.forParents) {
      groups = groups.filter((g) => !g.isDashboardLevel);

      if (this.dashboard.payload.type === 'predictive') {
        groups = groups.filter((g) => !g.isImpact);
      }
    }

    this.canAddToIPWG =
      this.widget.payload.type !== 'documentCount' ||
      (this.widget.payload.aggregator !== 'time' && this.widget.payload.graphType !== 'pie');

    const widgetHasSectionFilters = hasSectionFilters(this.widget);

    this.widgetGroups = groups.map((wg) => {
      let disabled = false;
      let tooltipText: string;

      const wgHasSectionFilter = !!getSectionFilters(wg.filters).length;
      if (widgetHasSectionFilters && wgHasSectionFilter) {
        disabled = true;

        tooltipText = translate('widgets.widget-group-not-applicable-selected');
      } else if (!this.canAddToIPWG && wg.isImpact) {
        disabled = true;

        tooltipText = translate('widgets.change-widget-group-dialog.cannot-add-volume');
      }

      return {
        title: wg.title,
        id: wg.id,
        disabled,
        tooltip: tooltipText ? { text: tooltipText } : undefined,
      };
    });

    if (this.widgetGroups.length) {
      this.currentGroupObject = groups.find(
        (group) =>
          group.widgets.includes(this.widget.id) ||
          (group.parentWidget && group.parentWidget === this.widget.id)
      );

      const selectedGroup =
        this.currentGroupObject?.id || !this.widgetGroups[0].disabled
          ? this.widgetGroups[0].id
          : null;

      this.changeDFGForm.setValue({
        filterGroup: selectedGroup,
      });
      this.changeDFGForm.get('filterGroup').setValidators(Validators.required);
      this.changeDFGForm.updateValueAndValidity();
    }

    if (this.forParents) {
      const dashboardLevelGroup = getDashboardLevelWidgetGroup(this.dashboard);

      if (dashboardLevelGroup?.widgets.includes(this.widget.id)) {
        this.currentGroupObject = dashboardLevelGroup;
      }
    }
  }

  get headerText() {
    if (this.isAdding) {
      return translate('widgets.change-widget-group-dialog.add-to-widget-group');
    }
    if (this.forParents) {
      return translate('widgets.change-widget-group-dialog.set-as-widget-group-parent');
    }
    return translate('widgets.change-widget-group-dialog.change-widget-group');
  }

  get descriptionText() {
    if (this.isAdding) {
      return translate('widgets.change-widget-group-dialog.add-to-widget-group-tooltip');
    }
    if (this.forParents) {
      return translate('widgets.change-widget-group-dialog.set-as-widget-group-parent-tooltip');
    }
    return translate('widgets.change-widget-group-dialog.change-widget-group-tooltip');
  }

  onCancel() {
    this.modalClosed.emit(null);
  }

  onSave() {
    const result = {};
    const selectedGroup = this.widgetGroups.filter(
      (group) =>
        group.title === this.changeDFGForm.get('filterGroup').value ||
        group.id === this.changeDFGForm.get('filterGroup').value
    )[0];
    let selectedGroupTitle = '';
    if (selectedGroup) {
      result['addToGroup'] = selectedGroup.id;
      selectedGroupTitle = selectedGroup.title;
    }

    if (!this.isAdding) {
      result['removeFromGroup'] = this.currentGroupObject?.id;
      if (selectedGroup.id !== this.currentGroupObject?.id) {
        this.alertService.showToast(
          translate('widgets.change-widget-group-dialog.widget-moved-to-widget-group', {
            widgetGroup: selectedGroupTitle,
          })
        );
      }
    } else {
      this.alertService.showToast(
        translate('widgets.change-widget-group-dialog.widget-added-to-widget-group', {
          widgetGroup: selectedGroupTitle,
        })
      );
    }

    if (this.forParents) {
      this.alertService.showToast(
        translate('widgets.change-widget-group-dialog.widget-set-as-parent', {
          widgetGroup: selectedGroupTitle,
        })
      );
    }
    this.modalClosed.emit(result);
  }
}
